import { isSyncActivityRecord, type SyncActivityRecord } from "~/helpers/sw/messageContract";

export const SYNC_ACTIVITY_LOG_LIMIT = 25;

export const SYNC_ACTIVITY_CACHE_NAME = "sw-sync-activity-log";

export const SYNC_ACTIVITY_LOG_KEY = "/__sw-sync-activity-log";

export interface SyncActivityLogStore {
	cacheName: string;
	cacheStorage: Pick<CacheStorage, "open">;
}

export const appendBoundedRecords = (
	existing: SyncActivityRecord[],
	record: SyncActivityRecord,
	limit: number = SYNC_ACTIVITY_LOG_LIMIT
): SyncActivityRecord[] => {
	const appended = [...existing, record];
	const overflow = appended.filter((entry) => entry.kind === record.kind).length - limit;
	if (overflow <= 0) return appended;
	let remaining = overflow;
	return appended.filter((entry) => {
		if (entry.kind !== record.kind || remaining === 0) return true;
		remaining -= 1;
		return false;
	});
};

export const parseSyncActivityRecords = (value: unknown): SyncActivityRecord[] =>
	Array.isArray(value) ? value.filter(isSyncActivityRecord) : [];

export const latestActivityOfKind = (
	records: SyncActivityRecord[],
	kind: SyncActivityRecord["kind"]
): SyncActivityRecord | null => {
	for (let index = records.length - 1; index >= 0; index -= 1) {
		const record = records[index];
		if (record.kind === kind) return record;
	}
	return null;
};

export const readSyncActivityLog = async ({
	cacheName,
	cacheStorage
}: SyncActivityLogStore): Promise<SyncActivityRecord[]> => {
	const cache = await cacheStorage.open(cacheName);
	const stored = await cache.match(SYNC_ACTIVITY_LOG_KEY);
	if (!stored) return [];
	try {
		return parseSyncActivityRecords(await stored.json());
	} catch {
		return [];
	}
};

export interface AppendSyncActivityInput extends SyncActivityLogStore {
	limit?: number;
	record: SyncActivityRecord;
}

const appendSyncActivity = async ({
	cacheName,
	cacheStorage,
	limit = SYNC_ACTIVITY_LOG_LIMIT,
	record
}: AppendSyncActivityInput): Promise<SyncActivityRecord[]> => {
	const existing = await readSyncActivityLog({ cacheName, cacheStorage });
	const next = appendBoundedRecords(existing, record, limit);
	const cache = await cacheStorage.open(cacheName);
	await cache.put(
		SYNC_ACTIVITY_LOG_KEY,
		new Response(JSON.stringify(next), { headers: { "content-type": "application/json" } })
	);
	return next;
};

export default appendSyncActivity;
